import { useState } from 'react';
import {
  DollarSign,
  Search,
  MapPin,
  Loader2,
  Save,
  RotateCcw,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useProducts, type Product } from '@/hooks/useProducts';
import { useLocations } from '@/hooks/useLocations';
import { useCurrency } from '@/hooks/useCurrency';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface PriceOverride {
  id: string;
  product_id: string;
  location_id: string;
  price_override: number | null;
  cost_override: number | null;
}

export default function Pricing() {
  const { products, isLoading: productsLoading } = useProducts();
  const { locations } = useLocations();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [editedPrices, setEditedPrices] = useState<Record<string, Record<string, string>>>({});

  // Fetch price overrides
  const { data: priceOverrides = [], isLoading: overridesLoading } = useQuery({
    queryKey: ['price-overrides'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_locations')
        .select('id, product_id, location_id, price_override, cost_override');
      if (error) throw error;
      return data as PriceOverride[];
    },
  });

  // Save price override mutation
  const savePriceOverride = useMutation({
    mutationFn: async ({ productId, locationId, price }: { productId: string; locationId: string; price: number }) => {
      const { error } = await supabase
        .from('product_locations')
        .upsert({
          product_id: productId,
          location_id: locationId,
          price_override: price,
        }, {
          onConflict: 'product_id,location_id',
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-overrides'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Price updated', { description: 'Location price override saved' });
    },
  });

  // Remove price override mutation
  const removePriceOverride = useMutation({
    mutationFn: async ({ productId, locationId }: { productId: string; locationId: string }) => {
      const { error } = await supabase
        .from('product_locations')
        .update({
          price_override: null,
          cost_override: null,
        })
        .eq('product_id', productId)
        .eq('location_id', locationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-overrides'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Price reset', { description: 'Location will use base price' });
    },
  });

  const getOverridePrice = (productId: string, locationId: string): number | null => {
    const override = priceOverrides.find(
      po => po.product_id === productId && po.location_id === locationId
    );
    return override?.price_override ?? null;
  };

  const isAssignedToLocation = (product: Product, locationId: string): boolean => {
    const productLocations = ((product as any).product_locations || []) as Array<{ location_id: string }>;
    if (productLocations.length > 0) {
      return productLocations.some((location) => location.location_id === locationId);
    }
    return product.location_id === locationId;
  };

  const getEditedPrice = (productId: string, locationId: string): string => {
    return editedPrices[productId]?.[locationId] ?? '';
  };

  const setEditedPrice = (productId: string, locationId: string, value: string) => {
    setEditedPrices(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [locationId]: value,
      },
    }));
  };

  const handleSavePrice = async (productId: string, locationId: string) => {
    const editedValue = getEditedPrice(productId, locationId);
    if (!editedValue) return;

    const price = parseFloat(editedValue);
    if (isNaN(price) || price < 0) {
      toast.error('Invalid price');
      return;
    }

    await savePriceOverride.mutateAsync({ productId, locationId, price });
    setEditedPrice(productId, locationId, '');
  };

  const handleResetPrice = async (productId: string, locationId: string) => {
    await removePriceOverride.mutateAsync({ productId, locationId });
    setEditedPrice(productId, locationId, '');
  };

  const filteredProducts = products.filter(product => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return product.name.toLowerCase().includes(query) ||
        product.sku?.toLowerCase().includes(query);
    }
    return true;
  });

  const filteredLocations = selectedLocation === 'all'
    ? locations
    : locations.filter(l => l.id === selectedLocation);

  const isLoading = productsLoading || overridesLoading;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Pricing Management</h2>
          <p className="text-sm text-muted-foreground">
            Set base prices and location-specific price overrides
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="flex-1 min-w-[200px] max-w-sm">
          <Label className="mb-1.5 block">Search products</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Name or SKU"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="min-w-[200px]">
          <Label className="mb-1.5 block">Location</Label>
          <Select value={selectedLocation} onValueChange={setSelectedLocation}>
            <SelectTrigger>
              <SelectValue placeholder="All locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {locations.map((location) => (
                <SelectItem key={location.id} value={location.id}>
                  {location.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Pricing Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading pricing...</span>
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3 sticky left-0 bg-muted/50">
                      Product
                    </th>
                    <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3">
                      Base Price
                    </th>
                    {filteredLocations.map(location => (
                      <th
                        key={location.id}
                        className="text-center text-sm font-medium text-muted-foreground px-4 py-3 min-w-[180px]"
                      >
                        <div className="flex items-center justify-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {location.name}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 sticky left-0 bg-background">
                        <div>
                          <p className="font-medium text-sm">{product.name}</p>
                          {product.sku && (
                            <p className="text-xs text-muted-foreground">{product.sku}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums">
                        {formatCurrency(product.price)}
                      </td>
                      {filteredLocations.map(location => {
                        const isAssigned = isAssignedToLocation(product, location.id);
                        const overridePrice = getOverridePrice(product.id, location.id);
                        const editedValue = getEditedPrice(product.id, location.id);
                        const hasOverride = overridePrice !== null;
                        const hasEdit = editedValue !== '';

                        return (
                          <td key={location.id} className="px-4 py-3">
                            {isAssigned ? (
                              <div className="flex items-center justify-center gap-2">
                                <div className="relative">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={editedValue || (hasOverride ? overridePrice.toFixed(2) : '')}
                                    onChange={(e) => setEditedPrice(product.id, location.id, e.target.value)}
                                    placeholder={product.price.toFixed(2)}
                                    className={cn(
                                      "w-24 pl-5 h-8 text-sm text-center",
                                      hasOverride && !hasEdit && "border-blue-500/50 bg-blue-50/50 dark:bg-blue-950/20"
                                    )}
                                  />
                                </div>
                                {(hasEdit || hasOverride) && (
                                  <div className="flex gap-1">
                                    {hasEdit && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7"
                                        onClick={() => handleSavePrice(product.id, location.id)}
                                        disabled={savePriceOverride.isPending}
                                      >
                                        <Save className="h-3 w-3 text-green-600" />
                                      </Button>
                                    )}
                                    {hasOverride && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7"
                                        onClick={() => handleResetPrice(product.id, location.id)}
                                        disabled={removePriceOverride.isPending}
                                        title="Reset to base price"
                                      >
                                        <RotateCcw className="h-3 w-3 text-muted-foreground" />
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-center text-xs text-muted-foreground">
                                Not assigned
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <div className="flex items-center gap-6 mt-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-blue-500/50 bg-blue-50/50 dark:bg-blue-950/20" />
          <span>Has custom price</span>
        </div>
        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          <span>Empty = uses base price</span>
        </div>
      </div>
    </div>
  );
}















